/* global fetch */

import { get, isEqual, isEmpty } from 'lodash';
import { setItem, getItem } from 'public/js/lib/local-storage';
import { PAYPAL_STORAGE_KEYS, DELIVERY_TYPE_FILTER } from './paypal-constants';
import { getCurrencyCode, mapMethodToShippingOption, getSessionUrls } from './paypal-utils';
import request from './request';

const buildShippingMethodsUrl = ({ country_code, postal_code }, selectedShippingOption, addressChanged, optionChanged) => {
	const sessionUrls = getSessionUrls();
	const baseUrl = get(sessionUrls, 'shippingUrl');

	const params = [];
	if (addressChanged) {
		params.push(`postalCode=${postal_code}`);
		params.push(`country=${country_code}`);
	}

	if (selectedShippingOption && optionChanged) {
		params.push(`shippingId=${get(selectedShippingOption, 'id')}`);
		params.push(`shippingAmount=${get(selectedShippingOption, 'amount.value')}`);
	}

	return get(params, 'length') ? `${baseUrl}?${params.join('&')}` : '';
};

const shippingUpdate = (data, actions) => {
	const shippingAddress = get(data, 'shipping_address', {});
	const cachedShippingAddress = getItem(PAYPAL_STORAGE_KEYS.address);
	const prevAddress = cachedShippingAddress ? JSON.parse(cachedShippingAddress) : {};
	const isNewAddress = isEmpty(prevAddress) || !isEqual(prevAddress.postal_code, shippingAddress.postal_code);

	const selectedShippingMethod = get(data, 'selected_shipping_option');
	const cachedShippingMethod = getItem(PAYPAL_STORAGE_KEYS.shippingMethod);
	const prevShippingMethod = cachedShippingMethod ? JSON.parse(cachedShippingMethod) : {};
	const isNewSelectedShippingMethod = isEmpty(prevShippingMethod) || !isEqual(prevShippingMethod, selectedShippingMethod);

	const shippingId = getItem(PAYPAL_STORAGE_KEYS.shippingMethodId);
	const isNewShippingId = isEmpty(shippingId) || shippingId !== selectedShippingMethod.id;

	//If the address has been updated save the new address to local storage
	if (shippingAddress && isNewAddress) {
		setItem(PAYPAL_STORAGE_KEYS.address, JSON.stringify(shippingAddress));
	}

	if (selectedShippingMethod && isNewSelectedShippingMethod) {
		setItem(PAYPAL_STORAGE_KEYS.shippingMethod, JSON.stringify(selectedShippingMethod));
	}

	const url = buildShippingMethodsUrl(shippingAddress, selectedShippingMethod, isNewAddress, (isNewShippingId && isNewSelectedShippingMethod));

	if(url) {
		return request(url)
			.then((response) => {
				const currencyCode = getCurrencyCode(shippingAddress.country_code);
				const methods = get(response, 'shippingMethods.methods', []);
				const shippingId = get(response, 'order.shippingId');
				setItem(PAYPAL_STORAGE_KEYS.shippingMethodId, shippingId);

				//Filter out pickup options for now, Format shipping methods to paypal obj
				const shippingOptions = sortShippingMethods(methods)
					//.filter(method => !DELIVERY_TYPE_FILTER.includes(method.deliveryType))
					.map((method, index) => mapMethodToShippingOption(method, index, shippingId, currencyCode));

				const shippingAmount = get(response, 'order.shippingAmount');
				const subtotalAmount = get(response, 'order.subtotalAmount');
				const taxAmount = get(response, 'order.taxAmount');
				const handlingAmount = get(response, 'order.handlingAmount');
				const totalAmount = get(response, 'order.totalAmount');
				const discountAmount = get(response, 'order.discountAmount');

				const patchData = [
					{
						op: 'replace',
						path: '/purchase_units/@reference_id==\'default\'/amount',
						value: {
							currency_code: currencyCode,
							value: totalAmount,
							breakdown: {
								item_total: {
									currency_code: currencyCode,
									value: subtotalAmount
								},
								tax_total: {
									currency_code: currencyCode,
									value: taxAmount
								},
								shipping: {
									currency_code: currencyCode,
									value: shippingAmount
								},
								handling: {
									currency_code: currencyCode,
									value: handlingAmount
								},
								discount: {
									currency_code: currencyCode,
									value: discountAmount,
								}
							}
						}
					},
				];

				if (shippingOptions && shippingOptions.length) {
					patchData.push(
						{
							op: selectedShippingMethod ? 'replace' : 'add', // or 'add' if there are none.
							path: `/purchase_units/@reference_id=='default'/shipping/options`,
							value: shippingOptions
						}
					);
				}

				//Patch the paypal order with the new shipping options
				return actions.order.patch(patchData);
			});
	} else {
		return actions.resolve();
	}
};

function sortShippingMethods(methods) {
	return methods
		// Sort options by ascending amount
		.sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount))
		// Move pre-defined non shipping items to end of array (Pickup)
		.filter(method => !DELIVERY_TYPE_FILTER.includes(method.deliveryType))
		.concat(methods.filter(method => DELIVERY_TYPE_FILTER.includes(method.deliveryType)));
}

module.exports = shippingUpdate;
